import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, of, startWith, switchMap, tap } from 'rxjs';
import { Asociacion, AsociacionesService, Asociado } from '../../../../api';
import { ItemComponent } from '../../item/item.component';
import { ItemType } from '../../formulario/request.model';
import { AsociadosListadoService, PaginaAsociados, TAMANOS_PAGINA } from '../../../services/asociados-listado.service';

export const DEBOUNCE_BUSQUEDA_MS = 300;

/**
 * Listado principal de asociados con paginación, búsqueda y filtro por asociación ejecutados en la API (MySQL).
 * - Búsqueda con debounce; cualquier cambio de filtro o de tamaño vuelve a la página 1.
 * - switchMap cancela la petición anterior si el usuario cambia rápido los filtros (nunca se mezclan respuestas viejas).
 * - Nunca descarga la lista completa de asociados: solo la página visible.
 */
@Component({
  selector: 'app-asociados-listado',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ItemComponent],
  templateUrl: './asociados-listado.component.html'
})
export class AsociadosListadoComponent implements OnInit, OnDestroy {
  readonly TAMANOS = TAMANOS_PAGINA;
  readonly ItemType = ItemType;

  readonly busqueda = new FormControl('', { nonNullable: true });
  asociaciones: Asociacion[] = [];
  asociacion: number | '' = '';
  pageSize = 25;
  pagina = 1;

  cargando = true;
  error = '';
  resultado: PaginaAsociados | null = null;

  private readonly peticion$ = new Subject<void>();
  private readonly suscripciones = new Subscription();

  constructor(
    private readonly listado: AsociadosListadoService,
    private readonly asociacionesService: AsociacionesService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  /**
   * La búsqueda, la asociación, la página y el tamaño se guardan en la URL (?q=&asociacion=&page=&size=) con replaceUrl: al volver
   * de una ficha (Volver, Atrás, F5) o abrir un enlace directo, el listado reaparece tal como se dejó.
   */
  private restaurarEstado(): void {
    const parametros = this.route.snapshot.queryParamMap;
    this.busqueda.setValue((parametros.get('q') ?? '').slice(0, 100), { emitEvent: false });
    const asociacion = Number(parametros.get('asociacion'));
    this.asociacion = Number.isInteger(asociacion) && asociacion > 0 ? asociacion : '';
    const tamano = Number(parametros.get('size'));
    this.pageSize = (TAMANOS_PAGINA as number[]).includes(tamano) ? tamano : 25;
    const pagina = Number(parametros.get('page'));
    this.pagina = Number.isInteger(pagina) && pagina > 0 ? pagina : 1;
  }

  private guardarEstado(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      replaceUrl: true,
      queryParamsHandling: 'merge',
      queryParams: {
        q: this.busqueda.value.trim() || null,
        asociacion: this.asociacion === '' ? null : this.asociacion,
        page: this.pagina > 1 ? this.pagina : null,
        size: this.pageSize !== 25 ? this.pageSize : null
      }
    });
  }

  ngOnInit(): void {
    this.restaurarEstado();
    this.suscripciones.add(
      this.peticion$.pipe(
        startWith(undefined),
        tap(() => { this.cargando = true; this.error = ''; this.guardarEstado(); }),
        // switchMap cancela la petición HTTP anterior al lanzar una nueva
        switchMap(() => this.listado.buscar({ page: this.pagina, pageSize: this.pageSize, q: this.busqueda.value, asociacion: this.asociacion === '' ? null : this.asociacion }).pipe(
          catchError((err: HttpErrorResponse) => {
            this.error = err?.error?.status?.message || err?.error?.message || 'No se han podido cargar los asociados.';
            return of(null);
          })
        ))
      ).subscribe(res => {
        this.cargando = false;
        if (!res) { this.resultado = null; return; }
        // Si la página pedida ya no existe (p. ej. tras cambiar los datos), se vuelve a la última
        if (!res.asociados.length && res.paginacion.total > 0 && this.pagina > res.paginacion.totalPages) {
          this.pagina = res.paginacion.totalPages;
          this.peticion$.next();
          return;
        }
        this.resultado = res;
      })
    );
    this.suscripciones.add(
      this.busqueda.valueChanges.pipe(debounceTime(DEBOUNCE_BUSQUEDA_MS), distinctUntilChanged()).subscribe(() => this.reiniciar())
    );
    this.asociacionesService.asociacionesGet().subscribe({
      next: res => { this.asociaciones = (res.asociaciones ?? []).slice().sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es')); },
      error: () => { this.asociaciones = []; }
    });
  }

  ngOnDestroy(): void { this.suscripciones.unsubscribe(); }

  /** Cambio de filtro o de tamaño: vuelve a la primera página. */
  reiniciar(): void { this.pagina = 1; this.peticion$.next(); }

  irA(pagina: number): void {
    const total = this.resultado?.paginacion.totalPages ?? 1;
    this.pagina = Math.min(Math.max(1, pagina), Math.max(1, total));
    this.peticion$.next();
  }

  reintentar(): void { this.peticion$.next(); }

  limpiarFiltros(): void {
    this.asociacion = '';
    this.busqueda.setValue('', { emitEvent: false });
    this.reiniciar();
  }

  get hayFiltros(): boolean { return this.busqueda.value.trim() !== '' || this.asociacion !== ''; }
  get total(): number { return this.resultado?.paginacion.total ?? 0; }
  get totalPaginas(): number { return this.resultado?.paginacion.totalPages ?? 0; }
  get desde(): number { return this.total ? (this.pagina - 1) * this.pageSize + 1 : 0; }
  get hasta(): number { return this.total ? Math.min(this.total, this.desde + (this.resultado?.asociados.length ?? 0) - 1) : 0; }
}
